export interface ConfigurationLabelValue {
	value: string;
	excludedFromDashboard?: boolean;
}

export interface ConfigurationLabel {
	showAsFilter?: boolean;
	label: string;
	displayName?: string;
	description?: string;
	values: (string | ConfigurationLabelValue)[];
}

export interface PlatformConfiguration extends Record<string, unknown> {
	ADDITIONAL_LABELS?: ConfigurationLabel[];
	CORE_LABELS?: ConfigurationLabel[];
	CONFIG?: {
		/**
		 * Space-separated Azure DevOps OAuth scope NAMES for this tenant (e.g.
		 * `vso.work_full vso.graph`). The fixed resource id is a code constant; each bare name is
		 * qualified with it. Overrides the `VITE_AZURE_SCOPES` env default; falls back to `.default`.
		 */
		AZURE_SCOPES?: string;
		/**
		 * Per-tenant switch that swaps the "Add to Jira" action for the Azure DevOps ticket creator.
		 * Managed in central config; replaces the former `ENABLE_AZURE_INTEGRATION` dev toggle.
		 */
		ENABLE_AZURE_INTEGRATION?: boolean;
		/**
		 * When true, bypasses Azure DevOps process rules when creating a work item.
		 * Passed directly to the `bypassRules` prop of `AzureDevopsTicketCreator`.
		 * The component defaults to `true` when absent.
		 */
		ENABLE_AZURE_BYPASS_RULE?: boolean;
	};
	DEV_TOGGLES?: {
		ENABLE_NEW_PLATFORM_DASHBOARD?: boolean;
		ENABLE_MOBILE_PLATFORM_DASHBOARD?: boolean;
		ENABLE_WEB_SDKS_BULK_EDIT: boolean;
		ENABLE_DEFAULT_JIRA_LABELS?: boolean;
		ENABLE_LABELS_MANAGEMENT?: boolean;
		ENABLE_JIRA_WRITE_BACK?: boolean;
	};
}

export interface LabelsExcludedFromDashboard {
	[labelKey: string]: Array<{ value: string; label: string }>;
}

export const isConfigurationLabel = (value: unknown): value is ConfigurationLabel => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const obj = value as Record<string, unknown>;
	return (
		'label' in obj && typeof obj.label === 'string' && 'values' in obj && Array.isArray(obj.values)
	);
};

export const isPlatformConfiguration = (value: unknown): value is PlatformConfiguration => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const obj = value as Record<string, unknown>;
	return (
		('ADDITIONAL_LABELS' in obj && Array.isArray(obj.ADDITIONAL_LABELS)) ||
		('CORE_LABELS' in obj && Array.isArray(obj.CORE_LABELS))
	);
};
